/* Applications — handover §14 step 8.

   "Criticality 1 is the list you read during an outage." So criticality is the
   default sort and the first lens, and the screen opens on what matters rather
   than alphabetically on what happens to start with an A.

   SSO and MFA are free text in the register because the real answers are things
   like 'SAML via Entra' and 'Enforced by CA policy', not booleans. They are
   still worth a tone: an application with no SSO is a separate password, and an
   application with no MFA is the one that gets somebody in. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { countdown, expiryTone, fmtDate, money } from '../../lib/format';
import type { Criticality, IApplication, Tone } from '../../models';
import { isAbsent } from '../../lib/registers';
import { applicationFields, valuesSeen } from '../../lib/registerFields';
import { recordHref, useOpenRecord } from '../../hooks/useOpenRecord';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  RelationCard,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  TableSkeleton,
  type Column,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import s from './screens.module.scss';

/* Criticality is 1 | 2 | 3 in the domain and '1 — Critical' in the SharePoint
   choice column — mappers.ts takes the leading digit on the way in. The words
   still have to come back for display, so they live here rather than being
   re-derived at three call sites. */
const CRIT_TONE: Record<Criticality, Tone> = { 1: 'bad', 2: 'warn', 3: 'muted' };
const CRIT_LABEL: Record<Criticality, string> = {
  1: '1 — Critical',
  2: '2 — Important',
  3: '3 — Useful',
};

export function ApplicationsScreen() {
  const svc = useServices();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<IApplication | null>(null);
  const [adding, setAdding] = useState(false);

  const apps = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });

  /* Arriving from a relation elsewhere. The value is whatever the source
     had to hand -- an id where one is stored, a name where it is not --
     so both have to match, and every record gets a URL somebody can send. */
  useOpenRecord(
    apps.data ?? [],
    (row, value) => row.id === value || row.name.toLowerCase() === value.toLowerCase(),
    setSelected,
  );
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  /* The index, never a secret — a credential record here says one exists and
     where it lives, which is the whole of section 16. */
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });

  const close = () => {
    setSelected(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.applications });
    void qc.invalidateQueries({ queryKey: qk.audit });
    close();
  };

  const save = useMutation({

    meta: { ok: 'Application saved' },
    mutationFn: (values: Record<string, unknown>) => {
      /* Criticality comes back off a select as a string. Left as one it would
         never equal 1, so every "criticality 1" count and lens would read zero
         while the row looked perfectly correct. */
      const patch = {
        ...values,
        crit: Number(values.crit ?? 3) as IApplication['crit'],
      } as unknown as Omit<IApplication, 'id'>;
      return selected ? svc.applications.update(selected.id, patch) : svc.applications.create(patch);
    },
    onSuccess: refresh,
  });

  const remove = useMutation({

    meta: { ok: 'Application deleted' },
    mutationFn: (id: string) => svc.applications.remove(id),
    onSuccess: refresh,
  });

  const personName = (id: string) =>
    (people.data ?? []).find((p) => p.id === id)?.name ?? (id ? '—' : '');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (apps.data ?? []).filter((a) => {
      if (lens === 'critical' && a.crit !== 1) return false;
      if (lens === 'nosso' && !isAbsent(a.sso)) return false;
      if (lens === 'nomfa' && !isAbsent(a.mfa)) return false;
      if (lens === 'renewing' && !(a.renews && a.renews !== '—')) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.purpose.toLowerCase().includes(q) ||
        a.supplier.toLowerCase().includes(q) ||
        a.host.toLowerCase().includes(q)
      );
    });
  }, [apps.data, lens, query]);

  const totals = useMemo(() => {
    const all = apps.data ?? [];
    return {
      critical: all.filter((a) => a.crit === 1).length,
      spend: all.reduce((n, a) => n + a.cost, 0),
      /* A critical application without MFA is the specific combination worth a
         number on the screen: the ones that matter most, protected least. */
      exposed: all.filter((a) => a.crit === 1 && isAbsent(a.mfa)).length,
    };
  }, [apps.data]);

  const columns: Column<IApplication>[] = [
    {
      key: 'name',
      header: 'Application',
      sortBy: (a) => a.name,
      render: (a) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-apps" aria-hidden />
          </span>
          <div>
            <div className={s.cellMain}>{a.name}</div>
            <div className={s.cellSub}>{a.purpose}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'crit',
      header: 'Criticality',
      sortBy: (a) => a.crit,
      render: (a) => <Badge tone={CRIT_TONE[a.crit]}>{CRIT_LABEL[a.crit]}</Badge>,
    },
    { key: 'supplier', header: 'Supplier', sortBy: (a) => a.supplier, render: (a) => a.supplier || '—' },
    {
      key: 'owner',
      header: 'Owners',
      sortBy: (a) => personName(a.bizOwner),
      render: (a) => (
        <>
          <div>{personName(a.bizOwner) || '—'}</div>
          <div className={s.cellSub}>IT: {personName(a.itOwner) || '—'}</div>
        </>
      ),
    },
    { key: 'users', priority: 3, header: 'Users', sortBy: (a) => a.users, render: (a) => a.users || '—' },
    {
      key: 'access',
      header: 'SSO / MFA',
      sortBy: (a) => `${isAbsent(a.sso) ? 1 : 0}${isAbsent(a.mfa) ? 1 : 0}`,
      render: (a) => (
        <div className={s.tags}>
          <Badge tone={isAbsent(a.sso) ? 'warn' : 'ok'}>
            {isAbsent(a.sso) ? 'No SSO' : a.sso}
          </Badge>
          <Badge tone={isAbsent(a.mfa) ? 'bad' : 'ok'}>
            {isAbsent(a.mfa) ? 'No MFA' : a.mfa}
          </Badge>
        </div>
      ),
    },
    {
      key: 'renews',
      header: 'Renews',
      sortBy: (a) => a.renews,
      render: (a) =>
        a.renews && a.renews !== '—' ? (
          <Badge tone={expiryTone(a.renews)}>{fmtDate(a.renews)}</Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'cost',
      priority: 3,
      header: 'A year',
      sortBy: (a) => a.cost,
      render: (a) => (a.cost > 0 ? money(a.cost) : <span className={s.cellSub}>—</span>),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Applications</h1>
          <p>
            What the business runs on, who owns it, where the data sits and how people get in.
            Sorted by criticality.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setSelected(null); setAdding(true); }}>
          New application
        </Button>
      </div>

      {apps.isError && <LoadError error={apps.error} what="applications" />}

      <StatGrid>
        <StatCard
          icon="ti-apps"
          label="Applications"
          value={apps.isLoading ? '—' : (apps.data?.length ?? 0)}
          tone="info"
          foot="On the register"
        />
        <StatCard
          icon="ti-alert-octagon"
          label="Criticality 1"
          value={apps.isLoading ? '—' : totals.critical}
          tone={totals.critical > 0 ? 'warn' : 'ok'}
          foot="The outage list"
        />
        <StatCard
          icon="ti-shield-off"
          label="Critical without MFA"
          value={apps.isLoading ? '—' : totals.exposed}
          tone={totals.exposed > 0 ? 'bad' : 'ok'}
          foot={totals.exposed > 0 ? 'Matter most, protected least' : 'All critical apps covered'}
        />
        <StatCard
          icon="ti-receipt"
          label="Committed a year"
          value={apps.isLoading ? '—' : money(totals.spend)}
          tone="info"
          foot="Across every application"
        />
      </StatGrid>

      <Controls count={`${rows.length} of ${apps.data?.length ?? 0}`}>
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Application, purpose, supplier or hosting…"
        />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'critical', label: 'Criticality 1' },
            { value: 'nomfa', label: 'No MFA' },
            { value: 'nosso', label: 'No SSO' },
            { value: 'renewing', label: 'Has a renewal' },
          ]}
        />
      </Controls>

      <Panel flush>
        {apps.isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(a) => a.id}
            initialSort={{ key: 'crit', dir: 'asc' }}
            onRowClick={setSelected}
            empty={
              <EmptyState icon="ti-apps" title="No applications match">
                Try clearing the filters.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null || adding}
        record={selected ? { ...selected, crit: String(selected.crit) } : null}
        fields={applicationFields({
          ...valuesSeen(apps.data, ['host', 'sso', 'mfa', 'data']),
          /* The supplier list comes from the supplier register rather than from
             what has been typed into applications before, so the two spellings
             of one company never both end up on offer. */
          supplier: (suppliers.data ?? []).map((x) => x.name),
        })}
        lookups={{ people: people.data ?? [], suppliers: suppliers.data ?? [] }}
        noun="application"
        icon="ti-apps"
        meta={
          selected && (
            <>
              <Badge tone={CRIT_TONE[selected.crit]}>{CRIT_LABEL[selected.crit]}</Badge>
              {selected.users > 0 && <Badge tone="muted">{selected.users} users</Badge>}
              {selected.renews && selected.renews !== '—' && (
                <Badge tone={expiryTone(selected.renews)}>{countdown(selected.renews)}</Badge>
              )}
            </>
          )
        }
        onClose={close}
        onSave={(values) => save.mutate(values)}
        onDelete={selected ? () => remove.mutate(selected.id) : undefined}
        saving={save.isPending}
        deleting={remove.isPending}
        error={save.error}
      >
        {selected && (() => {
          const supplier = (suppliers.data ?? []).find(
            (x) => x.id === selected.supplier || x.name === selected.supplier,
          );
          const byId = (id: string) => (people.data ?? []).find((p) => p.id === id);
          const owners = [
            { label: 'Business owner', person: byId(selected.bizOwner) },
            { label: 'IT owner', person: byId(selected.itOwner) },
          ].flatMap((o) => (o.person ? [{ label: o.label, person: o.person }] : []));
          const creds = (credentials.data ?? []).filter(
            (c) => c.system.toLowerCase() === selected.name.toLowerCase(),
          );

          return (
            <>
              <RelationCard
                icon="ti-building-store"
                title="Supplier"
                count={supplier ? 1 : 0}
                preview={supplier?.name ?? (selected.supplier || undefined)}
              >
                {supplier && (
                  <a className={`${s.row} ${s.rowFlush}`} href={recordHref('/suppliers', supplier.name)}>
                    <div className={s.rowIcon}>
                      <i className="ti ti-building-store" aria-hidden />
                    </div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{supplier.name}</div>
                      <div className={s.rowSub}>
                        {supplier.service} · {supplier.support || 'no support line recorded'}
                      </div>
                    </div>
                  </a>
                )}
              </RelationCard>

              <RelationCard
                icon="ti-user-check"
                title="Owners"
                count={owners.length}
                preview={owners.map((o) => o.person.name).join(' · ')}
              >
                {owners.map((o) => (
                  <a
                    key={o.label}
                    className={`${s.row} ${s.rowFlush}`}
                    href={recordHref('/people', o.person.upn)}
                  >
                    <div className={s.rowIcon}>
                      <i className="ti ti-user" aria-hidden />
                    </div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{o.person.name}</div>
                      <div className={s.rowSub}>
                        {o.label} · {o.person.title || 'No job title'}
                      </div>
                    </div>
                  </a>
                ))}
              </RelationCard>

              <RelationCard
                icon="ti-key"
                title="Credentials"
                count={creds.length}
                preview={creds.slice(0, 3).map((c) => c.name).join(' · ')}
              >
                {creds.map((c) => (
                  <a key={c.id} className={`${s.row} ${s.rowFlush}`} href={recordHref('/credentials', c.name)}>
                    <div className={s.rowIcon}>
                      <i className="ti ti-key" aria-hidden />
                    </div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{c.name}</div>
                      <div className={s.rowSub}>{c.type} · held in {c.vault}</div>
                    </div>
                  </a>
                ))}
              </RelationCard>
            </>
          );
        })()}

        {selected?.crit === 1 && isAbsent(selected.mfa) && (
          <div className={s.ownedNote}>
            <i className="ti ti-shield-off" aria-hidden />
            <span>
              Criticality 1 with no MFA recorded. If that is wrong it is worth correcting here,
              because this is the register anyone checks first when deciding what to harden.
            </span>
          </div>
        )}
      </EditDrawer>
    </>
  );
}
